import { convertFiltersToApiParams } from '@features/ui-filters/api/filterUtils';
import { sstFromBack } from '@root/src/entities/responseFormatters';
import { sstCommentFromBack } from '@root/src/entities/Sst/responseFormatter';
import { SstRequest } from '@root/src/entities/Sst/schema';
import { SstBack, SstFront, SstCommentBack, SstCommentFront } from '@root/src/entities/Sst/type';
import { PaginatedOptions, PaginatedResponse } from '@root/src/types/common';
import { mapPaginatedResponse } from '@root/src/utils/formatters/responseAdapters';
import { fetchRequest, RequestMethod } from '@services/api/utils/utils';

const getSsts = async ({
  filters = {},
  page = 1,
  pageSize = 15,
  sort = { field: 'createdAt', order: 'ASC' },
}: PaginatedOptions = {}): Promise<PaginatedResponse<SstFront>> =>
  fetchRequest<PaginatedResponse<SstBack>, PaginatedResponse<SstFront>>({
    endpoint: '/ssts',
    method: RequestMethod.GET,
    query: convertFiltersToApiParams(filters, {
      page,
      pageSize,
      // Inline each SST's space object so cards don't need one
      // GET /spaceobjects/:id per row (the fan-out OOM'd the backend).
      // Objects the caller cannot discover stay plain id strings.
      expand: 'spaceObject',
      sorted: [
        {
          id: sort.field,
          desc: sort.order,
        },
      ],
    }),
    responseFormatter: data => mapPaginatedResponse(data, sstFromBack),
  });

const getSst = async (id: string): Promise<SstFront> => {
  const result = await fetchRequest<SstBack, SstFront>({
    endpoint: `/ssts/${id}`,
    method: RequestMethod.GET,
    responseFormatter: sstFromBack,
  });
  return result;
};

/** Publishes a block: the request is what the form's `toSstRequest` built, sent as it is. */
const createSst = async (request: SstRequest): Promise<SstFront> =>
  fetchRequest<SstBack, SstFront, SstRequest>({
    endpoint: '/ssts',
    method: RequestMethod.POST,
    body: request,
    responseFormatter: sstFromBack,
  });

const createSstComment = async (id: string, comment: string): Promise<SstCommentFront> =>
  fetchRequest<SstCommentBack, SstCommentFront, { comment: string }>({
    endpoint: `/ssts/${id}/comments`,
    method: RequestMethod.POST,
    body: { comment },
    responseFormatter: sstCommentFromBack,
  });

const getSstComment = async (sstId: string, commentId: string): Promise<SstCommentFront> =>
  fetchRequest<SstCommentBack, SstCommentFront>({
    endpoint: `/ssts/${sstId}/comments/${commentId}`,
    method: RequestMethod.GET,
    responseFormatter: sstCommentFromBack,
  });

const updateSstComment = async (sstId: string, commentId: string, comment: string): Promise<SstCommentFront> =>
  fetchRequest<SstCommentBack, SstCommentFront, { comment: string }>({
    endpoint: `/ssts/${sstId}/comments/${commentId}`,
    method: RequestMethod.PUT,
    body: { comment },
    responseFormatter: sstCommentFromBack,
  });

const deleteSstComment = async (sstId: string, commentId: string): Promise<void> =>
  fetchRequest<void, void>({
    endpoint: `/ssts/${sstId}/comments/${commentId}`,
    method: RequestMethod.DELETE,
  });

const updateSstVisibility = async (
  sstId: string,
  data: { feedbackRequestedFrom: string[]; feedbackRequested?: boolean; critical?: boolean },
): Promise<SstFront> => {
  // Ensure all fields are included, even when they are false
  // Always send feedbackRequested and critical explicitly to avoid backend inferring them
  const body: {
    feedbackRequestedFrom: string[];
    feedbackRequested: boolean;
    critical: boolean;
  } = {
    feedbackRequestedFrom: data.feedbackRequestedFrom,
    feedbackRequested: data.feedbackRequested ?? false,
    critical: data.critical ?? false,
  };

  return fetchRequest<
    SstBack,
    SstFront,
    { feedbackRequestedFrom: string[]; feedbackRequested: boolean; critical: boolean }
  >({
    endpoint: `/ssts/${sstId}`,
    method: RequestMethod.PUT,
    body,
    responseFormatter: sstFromBack,
  });
};

const deleteSst = async (id: string): Promise<void> =>
  fetchRequest<void, void>({
    endpoint: `/ssts/${id}`,
    method: RequestMethod.DELETE,
  });

export const sstsApi = {
  getSsts,
  getSst,
  createSst,
  createSstComment,
  getSstComment,
  updateSstComment,
  deleteSstComment,
  updateSstVisibility,
  deleteSst,
};
